import { get } from 'react-hook-form';
import apiClient from './api/client';
import ENDPOINTS from './api/endpoints';

/**
 * Bill Service
 * Handles all Bill related API calls
 */

export const billService = {
  /**
   * Create new bill
   * @param {Object} billData - Bill data including items, table, payment details, etc.
   * @returns {Promise} Bill creation response
   */
  createBill: async (billData) => {
    try {
      const response = await apiClient.post(ENDPOINTS.BILLS.CREATE, billData);
      return response;
    } catch (error) {
      console.error('Failed to create bill:', error);
      throw error;
    }
  },

  /**
   * Get all bills
   * @returns {Promise} List of all bills
   */
  getAllBills: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_ALL);
      return response;
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      throw error;
    }
  },

  /**
   * Get bill by ID
   * @param {string} billId - Bill ID
   * @returns {Promise} Bill details
   */
  getBillById: async (billId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_BY_ID(billId));
      return response;
    } catch (error) {
      console.error(`Failed to fetch bill ${billId}:`, error);
      throw error;
    }
  },

  /**
   * Update bill status
   * @param {string} billId - Bill ID
   * @param {string} status - New status (pending, paid, cancelled)
   * @returns {Promise} Updated bill
   */
  updateBillStatus: async (billId, status) => {
    try {
      const response = await apiClient.patch(ENDPOINTS.BILLS.UPDATE_STATUS(billId), { status });
      return response;
    } catch (error) {
      console.error(`Failed to update bill ${billId} status:`, error);
      throw error;
    }
  },

  /**
   * Get bills by table ID
   * @param {string} tableId - Table ID
   * @returns {Promise} List of bills for the table
   */
  getBillsByTable: async (tableId) => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_BY_TABLE(tableId));
      return response;
    } catch (error) {
      console.error(`Failed to fetch bills for table ${tableId}:`, error);
      throw error;
    }
  },

  /**
   * Update payment mode for a bill
   * @param {string|number} billId - Bill ID
   * @param {string} paymentMode - New payment mode (CASH, UPI, CARD, BANK)
   * @param {number|null} [paymentAccountId] - Specific payment account to attribute the bill to
   * @returns {Promise} Update result
   */
  updatePaymentMode: async (billId, paymentMode, paymentAccountId = null) => {
    try {
      let url = `${ENDPOINTS.BILLS.UPDATE_PAYMENT_MODE(billId)}?paymentMode=${encodeURIComponent(paymentMode)}`;
      if (paymentAccountId != null) {
        url += `&paymentAccountId=${encodeURIComponent(paymentAccountId)}`;
      }
      const response = await apiClient.put(url);
      return response;
    } catch (error) {
      console.error(`Failed to update payment mode for bill ${billId}:`, error);
      throw error;
    }
  },

  getBillsTableSummary: async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_BILL_TABLE_SUMMARY);
      // const  response = await apiClient.get('http://localhost:8081/api/layout/table/bills-table');
      return response;
    } catch (error) {
      console.error('Failed to fetch bills table summary:', error);
      throw error;
    }
  },

  /**
   * Get pending bill by table ID and split
   * @param {string} tableId - Table ID
   * @param {string} tableSplit - Table split (A, B, C, etc.)
   * @returns {Promise} Pending bill for the table/split or null
   */
  getPendingBillByTableSplit: async (tableId, tableSplit) => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_PENDING_BY_TABLE_SPLIT(tableId, tableSplit));
      return response;
    } catch (error) {
      // 404 means no pending bill exists - this is expected
      if (error.response?.status === 404) {
        return null;
      }
      console.error(`Failed to fetch pending bill for table ${tableId}/${tableSplit}:`, error);
      throw error;
    }
  },

  /**
   * Get recent bills with orders
   * @param {Object} params - Query parameters { tableId, limit, days }
   * @returns {Promise} List of recent bills with full order details
   */
  getRecentBills: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.tableId) queryParams.append('tableId', params.tableId);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.days) queryParams.append('days', params.days);

      const endpoint = `${ENDPOINTS.BILLS.GET_RECENT}?${queryParams.toString()}`;
      const response = await apiClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch recent bills:', error);
      throw error;
    }
  },
  /**
   * Get ALL pending bills for a table and split (for split bill settlement)
   * @param {number} tableId
   * @param {string} tableSplit
   * @returns {Promise<Array>} List of pending bills
   */
  getAllPendingBills: async (tableId, tableSplit) => {
    try {
      const response = await apiClient.get(ENDPOINTS.BILLS.GET_ALL_PENDING(tableId, tableSplit));
      return response || [];
    } catch (error) {
      if (error.response?.status === 404) return [];
      console.error('Failed to fetch pending bills:', error);
      return [];
    }
  },

  /**
   * Cancel (permanently delete) a bill. Restores the stock it consumed and writes a
   * BILL_CANCEL audit entry server-side.
   *
   * The reason is mandatory — the backend rejects a blank one with 400. Since the bill
   * row itself is deleted, the audit entry is the only surviving explanation.
   *
   * @param {number|string} billId - Bill ID
   * @param {string} reason - Why the bill is being cancelled (truncated to 500 chars server-side)
   * @returns {Promise} Cancellation confirmation
   */
  cancelBill: (billId, reason) => {
    const query = new URLSearchParams({ reason: reason ?? '' }).toString();
    return apiClient.delete(`${ENDPOINTS.BILLS.CANCEL(billId)}?${query}`).then(r => r.data);
  },

    /**
     * Get audit logs (bill activity: cancel, reprint, payment-mode change, etc.)
     * Backend: GET /api/bills/audit-logs  (limit/offset paginated)
     * Response shape: { totalRecords: number, data: AuditLog[] }
     * @param {Object} params - { limit, offset, actionType, performedBy, performedFromDate, performedToDate, branchId }
     * @returns {Promise<{ totalRecords: number, data: Array }>}
     */
    getAuditLogs: async (params = {}) => {
      try {
        const queryParams = new URLSearchParams();

        if (params.actionType)        queryParams.append('actionType', params.actionType);
        if (params.performedBy)       queryParams.append('performedBy', params.performedBy);
        if (params.performedFromDate) queryParams.append('performedFromDate', params.performedFromDate);
        if (params.performedToDate)   queryParams.append('performedToDate', params.performedToDate);
        // Owners can narrow to one branch; for a branch user the backend forces its own
        // branch regardless of what is sent here.
        if (params.branchId)          queryParams.append('branchId', params.branchId);

        queryParams.append('limit', params.limit ?? 10);
        queryParams.append('offset', params.offset ?? 0);

        const endpoint = `${ENDPOINTS.BILLS.GET_AUDIT_LOGS}?${queryParams.toString()}`;
        const response = await apiClient.get(endpoint);
        return response;
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
        throw error;
      }
    },
};

export default billService;